import React, { useState, useEffect } from "react";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import moment from "moment";

interface NotificationItem {
  id: number;
  user_id: number;
  company_id: number;
  action: string;
  action_name: string;
  details: string;
  ip_address: string;
  user_agent: string;
  request_method: string;
  request_url: string;
  request_data: any;
  response_status: number;
  is_notified: number;
  is_read: number;
  created_at: string;
  updated_at: string;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const { t } = useTranslation("notifications");
  const userApi = useUserApi();
  const toast = useToaster();

  useEffect(() => {
    fetchNotifications(currentPage);
  }, [currentPage]);

  const fetchNotifications = (page: number) => {
    setLoading(true);

    const params = {
      per_page: 10,
      page: page,
    };

    userApi.callGetNotifications(
      params,
      (message: string, resp: any) => {
        console.log("Notifications API Response:", resp);

        // Handle the API response structure based on new format
        let newNotifications: NotificationItem[] = [];
        let paginationMeta: PaginationMeta | null = null;

        if (resp) {
          // New structure: resp.user_activities contains the notifications array
          if (Array.isArray(resp.user_activities)) {
            newNotifications = resp.user_activities;
            console.log("Setting notifications:", newNotifications.length, "items");
          }
          // New structure: resp.pagination contains the pagination info
          if (resp.pagination) {
            paginationMeta = resp.pagination;
            console.log("Setting pagination meta:", paginationMeta);
          }
        } else {
          console.warn("Unexpected response structure:", resp);
        }

        setNotifications(newNotifications);

        if (paginationMeta) {
          setMeta(paginationMeta);
        }

        setLoading(false);
        console.log("Final state - notifications count:", newNotifications.length);
      },
      (error: string) => {
        console.error("Error fetching notifications:", error);
        toast.error(t("errors.fetchFailed") + ": " + error);
        setNotifications([]);
        setLoading(false);
      }
    );
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format("MMM D, YYYY – h:mm A");
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    // Handle navigation based on notification request_data if needed
    // Currently no navigation as the API doesn't provide URL
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (meta?.last_page || 1)) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderPaginationNumbers = () => {
    if (!meta) return null;

    const pages = [];
    const maxVisiblePages = 5;
    const totalPages = meta.last_page;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            className={`page-num ${currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      }
    } else {
      // Show first page
      pages.push(
        <button key={1} className={`page-num ${currentPage === 1 ? "active" : ""}`} onClick={() => handlePageChange(1)}>
          1
        </button>
      );

      // Show dots if current page is far from start
      if (currentPage > 3) {
        pages.push(<span key="dots-start">...</span>);
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            className={`page-num ${currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      }

      // Show dots if current page is far from end
      if (currentPage < totalPages - 2) {
        pages.push(<span key="dots-end">...</span>);
      }

      // Show last page
      pages.push(
        <button
          key={totalPages}
          className={`page-num ${currentPage === totalPages ? "active" : ""}`}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="dashboard-main">
        <div className="dashboard-header">
          <h2>{t("pageTitle")}</h2>
          <p>{t("pageSubtitle")}</p>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-main">
      <div className="dashboard-header">
        <h2>{t("pageTitle")}</h2>
        <p>{t("pageSubtitle")}</p>
      </div>

      <div className="history-content">
        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th>{t("table.title")}</th>
                <th>{t("table.details")}</th>
                <th>{t("table.date")}</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notification) => (
                <tr
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={notification.is_read === 0 ? "unread" : ""}
                >
                  <td className="action">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>{notification.action}</span>
                      {notification.is_read === 0 && (
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            backgroundColor: "#3b82f6",
                            borderRadius: "50%",
                            display: "inline-block",
                          }}
                        ></span>
                      )}
                    </div>
                  </td>
                  <td className="details">{notification.details}</td>
                  <td className="date-time">{formatDate(notification.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.last_page > 1 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              {t("pagination.previous")}
            </button>
            <div className="page-numbers">{renderPaginationNumbers()}</div>
            <button
              className="page-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === meta.last_page}
            >
              {t("pagination.next")}
            </button>
          </div>
        )}

        {notifications.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔔</div>
            <h3>{t("noNotifications")}</h3>
            <p>{t("noNotificationsDesc")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
